import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Video, Loader2, Download, Sparkles, UserCircle, RefreshCw, ExternalLink
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  generateVideo, getAdminVideos,
  generateTestimonial, getTestimonials, getTestimonialStatus
} from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminVideos() {
  const [tab, setTab] = useState('sora');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1280x720');
  const [duration, setDuration] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Testimonial state
  const [script, setScript] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [genTest, setGenTest] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testLoading, setTestLoading] = useState(true);

  useEffect(() => {
    getAdminVideos().then(setVideos).catch(() => {}).finally(() => setLoading(false));
    getTestimonials().then(setTestimonials).catch(() => {}).finally(() => setTestLoading(false));
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a video prompt'); return; }
    setGenerating(true);
    toast.info('Video generation started. This may take 2-5 minutes...');
    try {
      await generateVideo({ prompt, size, duration });
      toast.success('Video generated!');
      setVideos(await getAdminVideos());
      setPrompt('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Video generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenTestimonial = async () => {
    if (!script.trim()) { toast.error('Enter a testimonial script'); return; }
    setGenTest(true);
    toast.info('Testimonial video generating via HeyGen...');
    try {
      const result = await generateTestimonial({ script, avatar_id: avatarId || undefined });
      toast.success(`Video queued! ID: ${result.heygen_video_id}`);
      setScript('');
      setTestimonials(await getTestimonials());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Testimonial generation failed');
    } finally {
      setGenTest(false);
    }
  };

  const refreshTestimonialStatus = async (id) => {
    try {
      const updated = await getTestimonialStatus(id);
      setTestimonials(prev => prev.map(t => t.id === id ? { ...t, ...updated } : t));
      if (updated.status === 'completed') toast.success('Video ready!');
      else toast.info(`Status: ${updated.status}`);
    } catch {
      toast.error('Failed to check status');
    }
  };

  const selectClass = "w-full h-12 bg-white/[0.06] border border-white/[0.08] text-white px-4 rounded-lg focus:border-rtb-purple focus:outline-none text-sm";
  const tabClass = (t) => `px-5 py-2.5 text-sm font-oswald font-bold uppercase tracking-wide rounded-lg transition-colors ${
    tab === t ? 'bg-rtb-purple text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
  }`;

  return (
    <AdminLayout title="AI Video Studio">
      {/* Tabs */}
      <div className="flex gap-2 mb-6" data-testid="video-tabs">
        <button onClick={() => setTab('sora')} className={tabClass('sora')} data-testid="tab-sora">
          Sora 2 Marketing
        </button>
        <button onClick={() => setTab('heygen')} className={tabClass('heygen')} data-testid="tab-heygen">
          HeyGen Testimonials
        </button>
      </div>

      {/* SORA TAB */}
      {tab === 'sora' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="p-6 bg-white/[0.04] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-rtb-purple/10 flex items-center justify-center rounded-lg">
                  <Sparkles className="w-5 h-5 text-rtb-purple" />
                </div>
                <div>
                  <h3 className="font-oswald text-lg font-bold">Generate Video</h3>
                  <p className="text-white/40 text-sm">Powered by Sora 2</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-white/60 mb-2 block text-sm">Prompt</Label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="w-full min-h-[120px] bg-white/[0.06] border border-white/[0.08] text-white p-3 rounded-lg focus:border-rtb-purple focus:outline-none text-sm"
                    placeholder="Describe the marketing video you want to create..."
                    data-testid="video-prompt"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/60 mb-2 block text-sm">Size</Label>
                    <select value={size} onChange={(e) => setSize(e.target.value)} className={selectClass} data-testid="video-size">
                      <option value="1280x720" className="bg-charcoal">HD (1280x720)</option>
                      <option value="1792x1024" className="bg-charcoal">Widescreen</option>
                      <option value="1024x1792" className="bg-charcoal">Portrait</option>
                      <option value="1024x1024" className="bg-charcoal">Square</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white/60 mb-2 block text-sm">Duration</Label>
                    <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} className={selectClass} data-testid="video-duration">
                      <option value="4" className="bg-charcoal">4 seconds</option>
                      <option value="8" className="bg-charcoal">8 seconds</option>
                      <option value="12" className="bg-charcoal">12 seconds</option>
                    </select>
                  </div>
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full bg-rtb-purple text-white hover:bg-rtb-purple/90 font-oswald font-bold uppercase rounded-lg py-3"
                  data-testid="generate-video-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating...</>
                  ) : (
                    <><Video className="w-5 h-5 mr-2" /> Generate Video</>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-oswald text-lg font-bold mb-4">Generated Videos</h3>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-rtb-purple animate-spin" />
              </div>
            ) : videos.length === 0 ? (
              <div className="p-12 bg-white/[0.04] border border-white/[0.06] rounded-xl text-center">
                <Video className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No videos generated yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {videos.map((v) => (
                  <motion.div
                    key={v.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-between"
                    data-testid={`video-${v.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        v.status === 'completed' ? 'bg-rtb-green/10' : v.status === 'generating' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                      }`}>
                        {v.status === 'completed' ? <Video className="w-5 h-5 text-rtb-green" /> :
                         v.status === 'generating' ? <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" /> :
                         <Video className="w-5 h-5 text-red-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{v.prompt}</p>
                        <p className="text-white/30 text-xs">{v.size} | {v.duration}s | {v.status}</p>
                      </div>
                    </div>
                    {v.status === 'completed' && (
                      <a href={`${BACKEND_URL}/api/admin/video/${v.id}`} target="_blank" rel="noopener noreferrer" className="ml-3">
                        <Button size="sm" variant="outline" className="border-white/10 text-white/60 hover:text-white rounded-lg">
                          <Download className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HEYGEN TAB */}
      {tab === 'heygen' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="p-6 bg-white/[0.04] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center rounded-lg">
                  <UserCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-oswald text-lg font-bold">Create Testimonial</h3>
                  <p className="text-white/40 text-sm">AI avatar via HeyGen</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-white/60 mb-2 block text-sm">Script</Label>
                  <textarea
                    value={script}
                    onChange={(e) => setScript(e.target.value)}
                    className="w-full min-h-[140px] bg-white/[0.06] border border-white/[0.08] text-white p-3 rounded-lg focus:border-rtb-purple focus:outline-none text-sm"
                    placeholder="Write the testimonial script for the AI avatar to speak..."
                    data-testid="testimonial-script"
                  />
                </div>
                <div>
                  <Label className="text-white/60 mb-2 block text-sm">Avatar ID (optional)</Label>
                  <input
                    value={avatarId}
                    onChange={(e) => setAvatarId(e.target.value)}
                    className="w-full h-12 bg-white/[0.06] border border-white/[0.08] text-white px-4 rounded-lg focus:border-rtb-purple focus:outline-none text-sm"
                    placeholder="Leave blank for default avatar"
                    data-testid="testimonial-avatar-id"
                  />
                </div>
                <Button
                  onClick={handleGenTestimonial}
                  disabled={genTest}
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-oswald font-bold uppercase rounded-lg py-3"
                  data-testid="generate-testimonial-btn"
                >
                  {genTest ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating...</>
                  ) : (
                    <><UserCircle className="w-5 h-5 mr-2" /> Generate Testimonial</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-oswald text-lg font-bold mb-4">Testimonial Videos</h3>
            {testLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-rtb-purple animate-spin" />
              </div>
            ) : testimonials.length === 0 ? (
              <div className="p-12 bg-white/[0.04] border border-white/[0.06] rounded-xl text-center">
                <UserCircle className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No testimonials yet</p>
                <p className="text-white/20 text-sm">Create your first AI avatar testimonial</p>
              </div>
            ) : (
              <div className="space-y-3">
                {testimonials.map((t) => (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-white/[0.04] border border-white/[0.06] rounded-xl"
                    data-testid={`testimonial-${t.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        t.status === 'completed' ? 'bg-emerald-500/10' : t.status === 'processing' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                      }`}>
                        {t.status === 'completed' ? <UserCircle className="w-5 h-5 text-emerald-400" /> :
                         t.status === 'processing' ? <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" /> :
                         <UserCircle className="w-5 h-5 text-red-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{t.script?.substring(0, 80)}...</p>
                        <p className="text-white/30 text-xs">
                          {t.status} | {t.avatar_id || 'default'} | {t.created_at?.substring(0, 16).replace('T', ' ')}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {t.status === 'processing' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/10 text-white/60 hover:text-white rounded-lg"
                            onClick={() => refreshTestimonialStatus(t.id)}
                            data-testid={`refresh-testimonial-${t.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        {t.status === 'completed' && t.video_url && (
                          <a href={t.video_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="border-white/10 text-white/60 hover:text-white rounded-lg">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                    {t.status === 'failed' && t.error && (
                      <p className="text-red-400/60 text-xs mt-2 pl-14">{t.error}</p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
